import { useEffect } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, HelpText, Input } from '#components/atoms/forms/index.ts';
import { RichList } from '#components/atoms/lists/rich-list/RichList.tsx';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { NO_ROLE_FLAG, SelectRole } from '#components/patterns/entity-select/role/index.ts';
import { CancelLinkButton } from '#components/patterns/forms/cancel-link-button/CancelLinkButton.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { Select } from '#components/patterns/forms/select/select/Select.tsx';
import { RoleName } from '#components/patterns/role-name/RoleName.tsx';
import { useTime } from '#hooks/useTime.ts';
import { useAddAPIKey, useEditAPIKey } from '#screens/settings/api-keys/form/APIKeyForm.queries.ts';
import { PermissionGroup } from '#screens/settings/api-keys/permission-group/PermissionGroup.tsx';
import type { NewKeyState } from '#screens/settings/api-keys/types.ts';
import { formatISODay } from '#util/time.ts';
import { checkIsFormValid } from '#util/validation/checkIsFormValid.ts';
import { buildExpiresAt, DEFAULT_EXPIRATION, expirationOptions, type ExpirationPreset } from './expiration.ts';
import { allPermissions, isAllowedForScopedKey } from './permissions.ts';
import styles from './APIKeyForm.module.css';

type NewAPIKeyProps = {
  id: null;
  /** Send the new api key's token to the parent, which will also close the modal */
  onSuccess: (newKey: NewKeyState) => void;
};

type EditAPIKeyProps = {
  id: string;
  defaultValues: {
    name: string;
    permissions: string[];
    roleID?: string | null;
  };
  onSuccess: () => void;
};

type Props = NewAPIKeyProps | EditAPIKeyProps;

type FormData = {
  keyName: string;
  expiration: ExpirationPreset;
  /** ISO day (yyyy-MM-dd) from the date input, only used when expiration is 'custom' */
  customExpirationDate: string;
  /** The role the key is scoped to, or NO_ROLE_FLAG for an unscoped key */
  roleID: string;
} & { [permission in (typeof allPermissions)[number]]: boolean };

/**
 * Given a list of enabled permissions, create a map of all permission names and boolean values,
 * setting to true any permissions in the provided list.  This is used to convert the API response
 * we get containing the enabled permissions to a shape that our form needs.
 */
function buildDefaultPermissionsMap(permissions: string[]) {
  return Object.fromEntries(
    allPermissions.map((p) => {
      return [p, permissions.includes(p)];
    })
  );
}

/**
 * Given the form's data, extract out the enabled permissions into a list of permission names.
 */
function buildPermissionsList(data: FormData) {
  return Object.entries(data)
    .filter(([name, checked]) => allPermissions.includes(name) && checked === true)
    .map(([pName]) => pName);
}

const DEFAULT_VALUES = {
  keyName: '',
  expiration: DEFAULT_EXPIRATION,
  customExpirationDate: '',
  roleID: NO_ROLE_FLAG,
  ...buildDefaultPermissionsMap([]),
};

/** The "unscoped" choice in the role-scope select.  NO_ROLE_FLAG keeps the form value a plain string. */
const UNSCOPED_OPTION = {
  value: NO_ROLE_FLAG,
  label: 'No role scope',
  hint: 'The key can use its permissions on every host',
};

/**
 * A form that is used to add and edit API keys.
 */
export function APIKeyForm(props: Props) {
  const { id, onSuccess } = props;

  const defaultValues =
    id ?
      {
        keyName: props.defaultValues.name,
        expiration: DEFAULT_EXPIRATION,
        customExpirationDate: '',
        roleID: props.defaultValues.roleID ?? NO_ROLE_FLAG,
        ...buildDefaultPermissionsMap(props.defaultValues.permissions),
      }
    : DEFAULT_VALUES;

  const methods = useForm<FormData>({
    mode: 'onChange',
    defaultValues,
  });
  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isDirty, isSubmitting, isSubmitted },
  } = methods;
  const isValid = checkIsFormValid(errors);
  const addAPIKey = useAddAPIKey();
  const editAPIKey = useEditAPIKey();
  const { data: { ROLE_SCOPED_API_KEYS: canScopeToRole } = {} } = useSuspenseFeatureFlagsQuery();

  // If there's no id specified, we're adding a new key.
  const isAddingNewKey = id === null;

  const expiration = useWatch({ control, name: 'expiration' });
  const roleID = useWatch({ control, name: 'roleID' });
  const isScoped = roleID !== NO_ROLE_FLAG;
  const isPermissionDisabled = isScoped ? (name: string) => !isAllowedForScopedKey(name) : undefined;
  const today = formatISODay(useTime());

  // A role-scoped key may only hold permissions on the scoped-key allow-list, so picking a scope
  // revokes anything else already checked.  Disabled permissions submit as undefined regardless,
  // but without this the checkbox would stay visually checked.
  useEffect(() => {
    if (!isScoped) {
      return;
    }
    for (const permission of allPermissions) {
      if (!isAllowedForScopedKey(permission)) {
        setValue(permission, false);
      }
    }
  }, [isScoped, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isAddingNewKey) {
        const apiKey = await addAPIKey.mutateAsync({
          name: data.keyName,
          permissions: buildPermissionsList(data),
          expiresAt: buildExpiresAt(data.expiration, data.customExpirationDate),
          roleID: data.roleID === NO_ROLE_FLAG ? undefined : data.roleID,
        });
        onSuccess({ token: apiKey.token, name: apiKey.name });
      } else {
        await editAPIKey.mutateAsync({
          id,
          values: {
            name: data.keyName,
            permissions: buildPermissionsList(data),
          },
        });
        onSuccess();
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          switch (path) {
            case 'name':
              setError('keyName', { message }, { shouldFocus: true });
              return true;
            case 'expiresAt':
              if (data.expiration === 'custom') {
                setError('customExpirationDate', { message }, { shouldFocus: true });
              } else {
                setError('expiration', { message }, { shouldFocus: true });
              }
              return true;
            case 'roleID':
              setError('roleID', { message }, { shouldFocus: true });
              return true;
            case undefined: // e.g. the user hit the max api key limit
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else {
        trackError(error);
        setError('root.global', { message: 'We couldn’t add the API key, please try again' });
      }
    }
  });

  const globalError = errors.root?.global?.message ?? 'Unable to save.  Please update and try again.';

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <Field label="Name" className="mb-40">
          <Input
            className="width-100"
            hasError={Boolean(errors.keyName)}
            {...register('keyName', { required: 'A name for the API key must be provided' })}
            autoComplete="off"
          />
          {errors.keyName && <ErrorMessage>{errors.keyName.message}</ErrorMessage>}
        </Field>
        {isAddingNewKey && (
          <>
            <Controller
              control={control}
              name="expiration"
              render={({ field }) => (
                <Select<ExpirationPreset>
                  label="Expiration"
                  className="mb-40"
                  defaultValue={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  innerRef={field.ref}
                  help={<HelpText>Expiration can’t be changed after the key is created.</HelpText>}
                  renderValue={(value) => expirationOptions.find((option) => option.value === value)?.label ?? null}
                  errorMessage={errors.expiration?.message}
                >
                  {expirationOptions.map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
            {expiration === 'custom' && (
              <Field label="Expiration date" className="mb-40">
                <Input
                  className="width-100"
                  type="date"
                  min={today}
                  hasError={Boolean(errors.customExpirationDate)}
                  {...register('customExpirationDate', {
                    validate: (value, formValues) => {
                      if (formValues.expiration !== 'custom') {
                        return true;
                      }
                      if (!value) {
                        return 'An expiration date must be provided';
                      }
                      if (value < today) {
                        return 'The expiration date must be in the future';
                      }
                      return true;
                    },
                  })}
                />
                {errors.customExpirationDate && <ErrorMessage>{errors.customExpirationDate.message}</ErrorMessage>}
              </Field>
            )}
            {canScopeToRole && (
              <SelectRole
                className="mb-40"
                control={control}
                name="roleID"
                label="Role scope"
                help={
                  <HelpText>
                    A role-scoped key can only see and manage hosts with its role. The scope can’t be changed after the
                    key is created.
                  </HelpText>
                }
                errorMessage={errors.roleID?.message}
                filterItems={(item) => item.value !== NO_ROLE_FLAG}
                additionalItems={[UNSCOPED_OPTION]}
              />
            )}
          </>
        )}
        {id && canScopeToRole && props.defaultValues.roleID ?
          <Field label="Role scope" className="mb-40">
            <HelpText>
              This key can only see and manage hosts with its role. The scope can’t be changed after the key is created.
            </HelpText>
            <RoleName roleID={props.defaultValues.roleID} />
          </Field>
        : null}
        <span className="label mb-16">Select scopes</span>
        {isScoped && (
          <HelpText className="mb-16">
            A role-scoped key can only be granted host permissions and read-only permissions, the rest are disabled.
          </HelpText>
        )}
        <RichList className={styles.APIKeyForm_richList}>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="hosts:*"
              groupDescription="View and manage hosts, lighthouses, and relays"
              permissions={[
                {
                  name: 'hosts:create',
                  description: 'Create hosts, lighthouses, and relays.  Enrollment requires hosts:enroll',
                },
                { name: 'hosts:read', description: 'Get the details of hosts, lighthouses, and relays' },
                { name: 'hosts:list', description: 'Get lists of hosts, lighthouses, and relays' },
                { name: 'hosts:update', description: 'Change hosts, lighthouses, and relays' },
                { name: 'hosts:delete', description: 'Remove hosts, lighthouses, and relays' },
                { name: 'hosts:enroll', description: 'Enroll hosts, lighthouses, and relays' },
                { name: 'hosts:debug', description: 'Debug hosts, lighthouses, and relays' },
                { name: 'hosts:block', description: 'Block hosts, lighthouses, and relays' },
                { name: 'hosts:unblock', description: 'Unblock hosts, lighthouses, and relays' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="join-keys:*"
              groupDescription="View and manage join keys for enrolling hosts"
              permissions={[
                {
                  name: 'join-keys:create',
                  description: 'Create join keys.  Anyone holding a join key can enroll hosts with it',
                },
                { name: 'join-keys:read', description: 'Get the details of join keys' },
                { name: 'join-keys:list', description: 'List existing join keys' },
                { name: 'join-keys:update', description: 'Rename join keys and change their expiration' },
                { name: 'join-keys:delete', description: 'Delete join keys' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="networks:*"
              groupDescription="View and manage networks"
              permissions={[
                { name: 'networks:create', description: 'Create a Managed Nebula network' },
                { name: 'networks:read', description: 'Get details about a Managed Nebula network' },
                { name: 'networks:list', description: 'Get list of Managed Nebula networks' },
                { name: 'networks:update', description: 'Change Managed Nebula networks' },
                { name: 'networks:delete', description: 'Remove Managed Nebula networks' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="roles:*"
              groupDescription="View and manage roles"
              permissions={[
                { name: 'roles:create', description: 'Create new roles' },
                { name: 'roles:read', description: 'Get the details of roles' },
                { name: 'roles:list', description: 'List existing roles' },
                { name: 'roles:update', description: 'Change roles' },
                { name: 'roles:delete', description: 'Delete unused roles' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="tags:*"
              groupDescription="View and manage tags"
              permissions={[
                { name: 'tags:create', description: 'Create new tags' },
                { name: 'tags:read', description: 'Get the details of tags' },
                { name: 'tags:list', description: 'List existing tags' },
                { name: 'tags:update', description: 'Change tags' },
                { name: 'tags:delete', description: 'Delete unused tags' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="routes:*"
              groupDescription="View and manage routes"
              permissions={[
                { name: 'routes:create', description: 'Create new routes' },
                { name: 'routes:read', description: 'Get the details of routes' },
                { name: 'routes:list', description: 'List existing routes' },
                { name: 'routes:update', description: 'Change routes' },
                { name: 'routes:delete', description: 'Delete routes' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="endpoint-oidc-providers:*"
              groupDescription="View and manage SSO providers for endpoint users"
              permissions={[
                { name: 'endpoint-oidc-providers:create', description: 'Create new endpoint SSO providers' },
                { name: 'endpoint-oidc-providers:read', description: 'Get the details of endpoint SSO providers' },
                { name: 'endpoint-oidc-providers:list', description: 'List existing endpoint SSO providers' },
                { name: 'endpoint-oidc-providers:update', description: 'Change endpoint SSO providers' },
                { name: 'endpoint-oidc-providers:delete', description: 'Delete endpoint SSO providers' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="domains:*"
              groupDescription="View and manage verified domains"
              permissions={[
                { name: 'domains:create', description: 'Add new domains' },
                { name: 'domains:read', description: 'Get the details of domains' },
                { name: 'domains:list', description: 'List existing domains' },
                { name: 'domains:delete', description: 'Delete domains' },
                { name: 'domains:verify', description: 'Verify ownership of domains' },
              ]}
            />
          </li>
          <li>
            <PermissionGroup
              isPermissionDisabled={isPermissionDisabled}
              groupLabel="audit-logs:*"
              groupDescription="View audit logs for your account"
              permissions={[{ name: 'audit-logs:list', description: 'Get a list of audit logs' }]}
            />
          </li>
        </RichList>
        <ConfirmChangesBanner
          isOpen={isDirty}
          title={isSubmitted && !isValid ? globalError : undefined}
          primaryButton={
            <Button variant="primary" type="submit" disabled={(!isAddingNewKey && !isDirty) || isSubmitting}>
              Save
            </Button>
          }
          secondaryButton={<CancelLinkButton fallbackTo="/settings/api-keys" />}
        />
      </form>
    </FormProvider>
  );
}
